import React, { useState, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, Shield, Zap } from 'lucide-react';
import { BoltIcon, ShieldCheckIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import './Login.css';
import logo from '../assets/logo.svg';

function Login({ onLogin, onSignupClick }) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formVisible, setFormVisible] = useState(false);

  useEffect(() => {
    setFormVisible(true);
  }, []);

  const validateForm = () => {
    const nextErrors = {};

    if (!email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!email.includes('@')) {
      nextErrors.email = 'Please enter a valid email';
    }

    if (!password) {
      nextErrors.password = 'Password is required';
    }

    return nextErrors;
  };

  const mapLoginError = (rawMessage) => {
    const message = (rawMessage || '').toLowerCase();

    if (message.includes('invalid login credentials') || message.includes('invalid credentials')) {
      return 'Incorrect email or password';
    }

    return 'Login failed. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    setAuthError('');
    setLoading(true);

    try {
      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors);
        setLoading(false);
        return;
      }

      // Sign in with Supabase Auth
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (authError) throw new Error(authError.message);

      // AuthContext onAuthStateChange handles setting user automatically
      onLogin({ email: data.user.email });
    } catch (err) {
      setAuthError(mapLoginError(err?.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container login-container">
      {/* Left Panel - Welcome Section */}
      <div className="auth-left-panel">
        <div className="auth-left-content">
          <div className="auth-header">
            <img src={logo} alt="LawEase Logo" className="auth-logo-img" />
            <h1 className="auth-app-name">LawEase</h1>
          </div>

          <div className="auth-subtitle">
            <p className="auth-tagline">Your AI-Powered Legal Assistant</p>
            <p className="auth-description">
              Get expert legal guidance, document automation, and case analysis in seconds.
            </p>
          </div>

          {/* Feature Highlights */}
          <div className="auth-features">
            <div className="feature-item">
              <div className="feature-icon"><BoltIcon className="w-4 h-4" /></div>
              <span>24/7 AI Support</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><ShieldCheckIcon className="w-4 h-4" /></div>
              <span>Secure & Private</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon"><ChartBarIcon className="w-4 h-4" /></div>
              <span>Instant Analysis</span>
            </div>
          </div>

          {/* Animated Illustration Area */}
          <div className="auth-illustration">
            <div className="illustration-card">
              <div className="illustration-badge"><Shield size={28} /></div>
              <div className="illustration-text">Secure</div>
            </div>
            <div className="illustration-card illustration-card-2">
              <div className="illustration-badge"><Lock size={28} /></div>
              <div className="illustration-text">Private</div>
            </div>
            <div className="illustration-card illustration-card-3">
              <div className="illustration-badge"><Zap size={28} /></div>
              <div className="illustration-text">Fast</div>
            </div>
          </div>


          {/* Decorative Shapes */}
          <div className="decorative-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
          </div>

          {/* Background Waves */}
          <div className="auth-waves">
            <div className="wave wave-1"></div>
            <div className="wave wave-2"></div>
            <div className="wave wave-3"></div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="auth-right-panel">
        <div className={`auth-form-wrapper ${formVisible ? 'visible' : ''}`}>
          <div className="auth-form-header">
            <h2>Welcome Back</h2>
            <p>Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form login-form">
            {/* Email Input */}
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <Mail className="input-icon" size={18} />
                <input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (authError) {
                      setAuthError('');
                    }
                    if (fieldErrors.email) {
                      setFieldErrors((prev) => ({ ...prev, email: '' }));
                    }
                  }}
                  disabled={loading}
                  className={`auth-input ${fieldErrors.email ? 'input-error' : ''}`}
                />
              </div>
              {fieldErrors.email && <p className="field-error-text">{fieldErrors.email}</p>}
            </div>

            {/* Password Input */}
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={18} />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (authError) {
                      setAuthError('');
                    }
                    if (fieldErrors.password) {
                      setFieldErrors((prev) => ({ ...prev, password: '' }));
                    }
                  }}
                  disabled={loading}
                  className={`auth-input ${fieldErrors.password ? 'input-error' : ''}`}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff size={22} />
                  ) : (
                    <Eye size={22} />
                  )}
                </button>
              </div>
              {fieldErrors.password && <p className="field-error-text">{fieldErrors.password}</p>}
              {authError && <div className="auth-credentials-error">{authError}</div>}
            </div>

            {/* Forgot Password */}
            <div className="form-footer">
              <a href="#forgot" className="forgot-password">
                Forgot password?
              </a>
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              className="auth-submit-btn"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="auth-footer-text">
            <p>
              Don't have an account?{' '}
              <button
                type="button"
                className="auth-link-btn"
                onClick={onSignupClick}
              >
                Create one
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;